import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CreateUserDTO, useGetRolesQuery } from '../../../../../api/controllers/admin-roles';

type ConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onCancel: () => void;
  isLoading: boolean;
  fields: CreateUserDTO | null;
  handleEditFields: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => void;
};

export const CreateUserModal: FC<ConfirmModalProps> = (props) => {
  const { open, onClose, onSuccess, onCancel, isLoading, fields, handleEditFields } = props;
  const { t } = useTranslation();
  const { data: roles, isLoading: rolesLoading } = useGetRolesQuery();

  const roleSelectOptions = roles?.roles.map((it) => {
    return (
      <MenuItem key={it.id} value={it.id}>
        {it.name}
      </MenuItem>
    );
  });

  // validation for required fields
  const requiredFields = ['role_id', 'email'] as (keyof CreateUserDTO)[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '' || fields[field] === null) {
        return true;
      }
    }

    return false;
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { minWidth: '380px' } }}>
      <DialogTitle>{t('users-and-roles.users.modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="email"
            value={fields?.email}
            onChange={handleEditFields}
            label={t('users-and-roles.users.modal.fields.email')}
            fullWidth
          />

          <TextField
            select
            name="role_id"
            value={fields?.role_id}
            onChange={handleEditFields}
            label={t('users-and-roles.users.modal.fields.role')}
            fullWidth
          >
            {roleSelectOptions}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onSuccess()}
          disabled={isLoading || rolesLoading || checkSubmitDisabling()}
        >
          {t('users-and-roles.users.modal.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onCancel()} disabled={isLoading || rolesLoading}>
          {t('users-and-roles.users.modal.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
